<template>
  <div>
    <load-profile></load-profile>

    <!-- Main Card -->
    <b-card no-body>
      <b-card-header class="border-bottom">
        <div class="d-flex justify-content-between align-items-center" :style="{ width: '100%' }">
          <h5 class="mb-0">
            <feather-icon icon="ShieldIcon" class="mr-1" size="20" />
            {{ $t('general.whitelistIPManagement') }}
          </h5>
          <b-button v-if="this.config && this.config.ips && this.config.ips.length > 0" v-ripple.400="'rgba(255, 255, 255, 0.15)'" variant="primary" size="sm" @click="openEditModal(true)">
            <feather-icon icon="EditIcon" class="mr-1" size="16" />
            {{ $t('button.edit') }}
          </b-button>
        </div>
      </b-card-header>

      <b-card-body style="min-height: 250px;">
        <b-overlay style="min-height: 250px;" :show="loading" spinner-variant="primary" spinner-small rounded="sm">
          <b-row v-if="config && config.ips">
            <b-col cols="12">
              <h6 class="text-muted mb-2 mt-1">{{ $t('field.ipAddress') }}</h6>
              <div class="ip-list-container">
                <div v-for="(ip, index) in ipList" :key="index" class="ip-badge mb-2 mr-2">
                  <div class="d-flex align-items-center">
                    <feather-icon icon="ServerIcon" size="16" class="mr-1" />
                    <span>{{ ip.trim() }}</span>
                  </div>
                </div>
              </div>
            </b-col>
          </b-row>

          <b-row v-else-if="!loading && config && !config.ips">
            <b-col cols="12">
              <div class="text-center p-5">
                <feather-icon icon="AlertCircleIcon" size="32" class="mb-2" />
                <h5>{{ $t('general.noWhitelistIPFound') }}</h5>
                <p class="text-muted">{{ $t('general.addWhitelistIPDescription') }}</p>
                <b-button v-ripple.400="'rgba(255, 255, 255, 0.15)'" variant="primary" @click="openEditModal(false)">
                  <feather-icon icon="PlusIcon" class="mr-1" />
                  {{ $t('button.addNew') }}
                </b-button>
              </div>
            </b-col>
          </b-row>
        </b-overlay>
      </b-card-body>
    </b-card>

    <!-- Edit Modal -->
    <b-modal ref="ipModal" :title="$t('breadcrumb.whitelistIP')" hide-footer centered size="lg">
      <b-form @submit.prevent="saveWhitelistConfig">
        <b-row>
          <b-col cols="12">
            <b-form-group :label="$t('field.ipAddress') || 'IP Addresses'">
              <div class="tag-input-wrapper">
                <div class="tag-input" :class="{ 'is-invalid': showError }">
                  <span v-for="(ip, index) in ipAddresses" :key="`tag-${index}`" class="tag">
                    {{ ip }}
                    <span class="tag-close" @click="removeIpAddress(index)">&times;</span>
                  </span>
                  <input
                    type="text"
                    ref="tagInput"
                    v-model="currentIpInput"
                    @keydown.enter.prevent="addIpAddress"
                    @input="validateIpInput"
                    @blur="validateOnBlur"
                    @keydown.delete="handleDelete"
                    @keydown.down="showSuggestion = isValidInput"
                    :placeholder="ipAddresses.length === 0 ? ($t('placeholder.enterIPAddress') || 'Enter IP address or subnet') : ''"
                    class="tag-input-field"
                  />
                </div>
                <div v-if="showSuggestion && isValidInput" class="tag-suggestion">
                  <div class="suggestion-item" @click="addIpAddress">
                    <span class="mr-1"><feather-icon icon="PlusIcon" size="16" /></span>
                    <span>{{ currentIpInput }}</span>
                  </div>
                </div>
                <div v-if="showError" class="invalid-feedback d-block">
                  <feather-icon icon="AlertTriangleIcon" size="14" class="mr-1" />
                  {{ $t('error.invalidIPAddress') || 'Invalid IP address format' }}
                </div>
              </div>
              <small class="form-text text-muted mt-1">
                {{ $t('general.enterValidIPOrSubnet') }}
              </small>
            </b-form-group>
          </b-col>

          <b-col cols="12" class="text-right">
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              class="mr-1"
              variant="outline-secondary"
              @click="$refs.ipModal.hide()"
            >
              {{ $t('button.cancel') }}
            </b-button>
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              type="submit"
              variant="primary"
              :disabled=" saveLoading || showError || (!isEdited && ipAddresses.length === 0)"
              @click="saveWhitelistConfig"
            >
              <b-spinner small v-if="saveLoading" class="mr-1"></b-spinner>
              {{ $t('button.saveChanges') || 'Save Changes' }}
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BCardHeader,
  BCardBody,
  BButton,
  BModal,
  BOverlay,
  BForm,
  BFormGroup,
  BSpinner,
} from 'bootstrap-vue'
import LoadProfile from '@/components/LoadProfile.vue'
import Ripple from 'vue-ripple-directive'
import Repository from '@/repositories/RepositoryFactory';

const profileRepository = Repository.get('profile')

export default {
  name: 'WhitelistIP',
  components: {
    BRow,
    BCol,
    BCard,
    BCardHeader,
    BCardBody,
    BButton,
    BModal,
    BOverlay,
    BForm,
    BFormGroup,
    BSpinner,
    LoadProfile,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      config: null,
      formData: {},
      loading: false,
      saveLoading: false,
      ipAddresses: [],
      currentIpInput: '',
      isValidInput: false,
      showSuggestion: false,
      showError: false,
      inputTouched: false,
      isEdited: false,
    }
  },
  computed: {
    ipList() {
      return this.config && this.config.ips
        ? this.config.ips.split(',').map(ip => ip.trim())
        : []
    },
  },
  mounted() {
    this.fetchWhitelistConfig()
  },
  methods: {
    fetchWhitelistConfig() {
      this.loading = true
      profileRepository
        .getProfile()
        .then(response => {
          this.config = {
            ips: response.data.data.whiteIps,
          }
        })
        .catch(error => {
          console.error('Error fetching whitelist config:', error)
        })
        .finally(() => {
          this.loading = false
        })
    },
    openEditModal(isEdited = false) {
      this.ipAddresses = this.config && this.config.ips ? this.config.ips.split(',').map(ip => ip.trim()) : []
      this.currentIpInput = ''
      this.isValidInput = false
      this.showSuggestion = false
      this.showError = false
      this.inputTouched = false
      this.isEdited = isEdited

      this.$refs.ipModal.show()
      this.$nextTick(() => {
        if (this.$refs.tagInput) {
          this.$refs.tagInput.focus()
        }
      })
    },
    saveWhitelistConfig() {
      this.saveLoading = true
      const ips = this.ipAddresses.join(', ')

      profileRepository.updateWhitelistIP({
        whiteIps: ips,
      })
        .then(() => {
          this.fetchWhitelistConfig()
          this.$refs.ipModal.hide()
        })
        .catch(error => {
          console.error('Error saving whitelist config:', error)
        })
        .finally(() => {
          this.saveLoading = false
        })
    },
    validateIpInput() {
      // Basic IP validation regex (IPv4 and subnet format)
      const ipv4Regex = /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/
      this.isValidInput = ipv4Regex.test(this.currentIpInput)
      this.showSuggestion = this.isValidInput && this.currentIpInput.trim() !== ''

      // If input is empty or valid, remove error message immediately while typing
      if (this.currentIpInput.trim() === '' || this.isValidInput) {
        this.showError = false
      } else if (this.inputTouched && !this.isValidInput) {
        // Only show error on typing if field has been touched before (lost focus)
        this.showError = true
      }
    },
    validateOnBlur() {
      this.inputTouched = true
      if (this.currentIpInput.trim() !== '') {
        this.showError = !this.isValidInput
      } else {
        // If input is empty, no need to show error
        this.showError = false
      }
    },
    addIpAddress() {
      if (this.currentIpInput.trim() && this.isValidInput) {
        if (!this.ipAddresses.includes(this.currentIpInput.trim())) {
          this.ipAddresses.push(this.currentIpInput.trim())
        }
        this.currentIpInput = ''
        this.isValidInput = false
        this.showSuggestion = false
        this.showError = false
        this.inputTouched = false
        this.$nextTick(() => {
          if (this.$refs.tagInput) {
            this.$refs.tagInput.focus()
          }
        })
      } else if (this.currentIpInput.trim() && !this.isValidInput) {
        this.inputTouched = true
        this.showError = true
      }
    },
    removeIpAddress(index) {
      this.ipAddresses.splice(index, 1)
      // Reset error state when removing IPs
      if (this.currentIpInput.trim() === '') {
        this.showError = false
      }
      // Focus back on input after removing an IP
      this.$nextTick(() => {
        if (this.$refs.tagInput) {
          this.$refs.tagInput.focus()
        }
      })
    },
    handleDelete(e) {
      // If input is empty and there are tags, delete the last tag
      if (this.currentIpInput === '' && this.ipAddresses.length > 0 && e.target.selectionStart === 0) {
        this.ipAddresses.pop()
      }
    },
  },
  setup() {
    const permissionType = 'whitelist-ip'
    return { permissionType }
  },
}
</script>

<style lang="scss" scoped>
.ip-list-container {
  display: flex;
  flex-wrap: wrap;
}

.ip-badge {
  background-color: rgba(var(--primary), 0.12);
  color: var(--primary);
  border-radius: 0.35rem;
  padding: 0.4rem 0.8rem;
  font-family: monospace;
  font-size: 0.9rem;
  display: inline-flex;
  align-items: center;
}

.tag-input-wrapper {
  position: relative;
}

.tag-input {
  display: flex;
  flex-wrap: wrap;
  min-height: 40px;
  padding: 0.25rem 0.5rem;
  border: 1px solid #d8d6de;
  border-radius: 0.357rem;
  background-color: #fff;
  cursor: text;
  align-items: center;
}

.tag-input:focus-within {
  border-color: var(--primary);
  box-shadow: 0 3px 10px 0 rgba(34, 41, 47, 0.1);
}

.tag-input.is-invalid {
  border-color: #ea5455;
}

.tag {
  display: inline-flex;
  align-items: center;
  background-color: rgba(var(--primary), 0.12);
  color: var(--primary);
  border-radius: 0.25rem;
  padding: 0.25rem 0.5rem;
  margin: 0.25rem;
  font-family: monospace;
  font-size: 0.9rem;
  max-width: 100%;
  white-space: nowrap;
}

.tag-close {
  margin-left: 0.35rem;
  cursor: pointer;
  font-weight: bold;
  font-size: 1.1rem;
}

.tag-input-field {
  flex: 1;
  min-width: 8rem;
  border: none;
  outline: none;
  padding: 0.35rem;
  font-size: 0.9rem;
  background: transparent;
}

.tag-suggestion {
  position: absolute;
  width: 100%;
  background: white;
  border: 1px solid #d8d6de;
  border-top: none;
  border-radius: 0 0 0.357rem 0.357rem;
  box-shadow: 0 4px 10px 0 rgba(34, 41, 47, 0.1);
  z-index: 10;
  max-height: 150px;
  overflow-y: auto;
}

.suggestion-item {
  padding: 0.75rem 1rem;
  cursor: pointer;
  font-family: monospace;
}

.suggestion-item:hover {
  background-color: rgba(var(--primary), 0.05);
}

.invalid-feedback {
  color: #ea5455;
  font-size: 0.857rem;
  margin-top: 0.25rem;
  display: flex;
  align-items: center;
}
</style>
